export const RIB_REMINDER_DELAY_DAYS = 30;

/** Périmètre Standard par défaut (env backend STANDARD_ALLOWED_COMPANY_NAMES). */
export const DEFAULT_STANDARD_ALLOWED_COMPANY_NAMES = [
    'Flower (test)',
    'Standard_option (test)',
];

export const formatDateDdMmYyyy = (value) => {
    if (value == null || value === '') return '—';
    const raw = String(value).trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
        const [year, month, day] = raw.split('-');
        return `${day}/${month}/${year}`;
    }
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(raw)) return raw;
    return raw;
};

export const getParisTodayIso = () => new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/Paris',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
}).format(new Date());

export const shiftIsoDate = (isoDate, dayOffset) => {
    const [year, month, day] = isoDate.split('-').map(Number);
    const date = new Date(Date.UTC(year, month - 1, day));
    date.setUTCDate(date.getUTCDate() + dayOffset);
    return date.toISOString().slice(0, 10);
};

/** Aligné sur le backend : today - (RIB_REMINDER_DELAY_DAYS + 1) jours (Europe/Paris). */
export const getLatestAllowedReminderDateEnd = (todayIso = getParisTodayIso()) => (
    shiftIsoDate(todayIso, -(RIB_REMINDER_DELAY_DAYS + 1))
);

const extractIsoDate = (text, patterns) => {
    if (!text) return null;
    for (const pattern of patterns) {
        const match = String(text).match(pattern);
        if (match?.[1]) return match[1];
    }
    return null;
};

const normalizeDetail = (detail) => {
    if (detail == null) return { text: '', data: null };
    if (typeof detail === 'string') return { text: detail, data: null };
    if (typeof detail === 'object') {
        const text = [
            detail.title,
            detail.message,
            detail.msg,
            detail.detail,
        ].filter(Boolean).join(' ');
        return { text, data: detail };
    }
    return { text: String(detail), data: null };
};

const pickIsoDate = (...candidates) => {
    for (const value of candidates) {
        if (value == null || value === '') continue;
        const raw = String(value).trim();
        if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;
        const fromSlash = raw.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
        if (fromSlash) {
            return `${fromSlash[3]}-${fromSlash[2]}-${fromSlash[1]}`;
        }
    }
    return null;
};

export const parseBankDetailsRemindersError = (error, formValues = {}) => {
    const responseData = error?.response?.data && typeof error.response.data === 'object'
        ? error.response.data
        : (error && typeof error === 'object' && error.code ? error : null);

    if (responseData?.code === 'FORBIDDEN_COMPANY') {
        const allowed = Array.isArray(responseData?.error?.allowed_company_names)
            ? responseData.error.allowed_company_names
            : DEFAULT_STANDARD_ALLOWED_COMPANY_NAMES;
        return {
            kind: 'forbidden_company',
            message: responseData.message || null,
            companyName: responseData?.error?.company_name
                || responseData?.filters?.company_name
                || formValues.companyName
                || null,
            allowedCompanyNames: allowed,
        };
    }

    const { text: detailText, data: detailData } = normalizeDetail(
        responseData?.detail ?? error?.response?.data?.detail
    );

    const todayParis = pickIsoDate(
        detailData?.today_paris,
        detailData?.today_date,
        responseData?.filters?.today_paris,
        extractIsoDate(detailText, [/aujourd'hui\s*\((\d{4}-\d{2}-\d{2})\)/i]),
    ) || getParisTodayIso();

    const selectedEndDate = pickIsoDate(
        detailData?.selected_end_date,
        detailData?.reminder_date_end,
        responseData?.filters?.reminder_date_end,
        extractIsoDate(detailText, [
            /reminder_date_end\s*\((\d{4}-\d{2}-\d{2})\)/i,
            /Date de fin sélectionnée\s*:\s*(\d{2}\/\d{2}\/\d{4})/i,
            /Date de fin sélectionnée\s*:\s*(\d{4}-\d{2}-\d{2})/i,
        ]),
        formValues.reminderDateEnd,
    );

    const maxAllowedDate = pickIsoDate(
        detailData?.max_allowed_date,
        detailData?.latest_allowed_trip_end,
        responseData?.filters?.latest_allowed_reminder_date_end,
        extractIsoDate(detailText, [
            /Date maximale acceptée\s*:\s*(\d{4}-\d{2}-\d{2})/i,
            /Date maximale autorisée\s*:\s*(\d{4}-\d{2}-\d{2})/i,
            /Date maximale autorisée\s*:\s*(\d{2}\/\d{2}\/\d{4})/i,
            /antérieure ou égale au\s+(\d{2}\/\d{2}\/\d{4})/i,
        ]),
    ) || getLatestAllowedReminderDateEnd(todayParis);

    if (
        detailText.includes('reminder_date_start doit être')
        || detailText.includes('Période invalide : reminder_date_start')
        || detailData?.code === 'invalid_period_order'
    ) {
        return {
            kind: 'invalid_period_order',
            selectedStartDate: pickIsoDate(
                detailData?.reminder_date_start,
                responseData?.filters?.reminder_date_start,
                formValues.reminderDateStart
            ),
            selectedEndDate: pickIsoDate(
                detailData?.reminder_date_end,
                responseData?.filters?.reminder_date_end,
                selectedEndDate
            ),
        };
    }

    if (
        detailText.includes('au moins un mois')
        || detailText.includes('Date maximale acceptée')
        || detailText.includes('Date maximale autorisée')
        || detailText.includes('Période de fin de séjour invalide')
        || detailText.includes('trop récente')
        || detailData?.code === 'invalid_trip_end_period'
        || (selectedEndDate && selectedEndDate > maxAllowedDate)
    ) {
        return {
            kind: 'invalid_trip_end_period',
            selectedEndDate,
            todayParis,
            maxAllowedDate,
        };
    }

    const genericMessage = responseData?.message
        || detailData?.message
        || (detailText && !detailText.startsWith('Erreur de validation') ? detailText : null)
        || error?.message
        || null;

    return {
        kind: 'generic',
        message: genericMessage,
    };
};

export const buildLocalTripEndPeriodError = (reminderDateEnd) => ({
    kind: 'invalid_trip_end_period',
    selectedEndDate: reminderDateEnd?.trim() || null,
    todayParis: getParisTodayIso(),
    maxAllowedDate: getLatestAllowedReminderDateEnd(),
});

export const buildLocalPeriodOrderError = (reminderDateStart, reminderDateEnd) => ({
    kind: 'invalid_period_order',
    selectedStartDate: reminderDateStart?.trim() || null,
    selectedEndDate: reminderDateEnd?.trim() || null,
});

export const isReminderDateAfterLatestAllowed = (isoDate) => {
    if (!isoDate || String(isoDate).trim() === '') return false;
    return String(isoDate).trim() > getLatestAllowedReminderDateEnd();
};
